import { core, formatDay, type GoalProgress } from '@map-my-money/engine';
import { lastDayOf, linePoints } from './line.ts';
import { definitionsFor } from './definitions.ts';
import { goalView } from './goals.ts';
import { money, round1 } from './money.ts';
import { mortgageFreeDate } from './mortgage.ts';
import { nowState } from './now.ts';
import { lastAmount, runForecast, type Forecasted, type ViewInput } from './run.ts';

export type Resolution = 'yearly' | 'monthly';

export interface ForecastOptions {
  readonly years?: number;
  readonly resolution?: Resolution;
  readonly maxPoints?: number;
}

export const FORECAST_DEFAULTS = { resolution: 'yearly' as Resolution, maxPoints: 120 };

export interface MilestoneView {
  readonly goal_id: string;
  readonly label: string;
  readonly date: string;
  /** The goal's series; `debt` for the debt-free milestone that is not a goal. */
  readonly series: string;
}

/** As engine goal-tracker's isDebtFreeGoal. */
const isDebtFreeGoal = (goal: GoalProgress['goal']): boolean => goal.series === 'debt' && goal.operation === 'crossunder' && core.isZero(goal.targetValue);

/**
 * Goals reached inside the forecast's months and not met now, then the debt-free date when no
 * debt-free goal already marks it, by date.
 */
export function milestones(f: Forecasted): MilestoneView[] {
  const c = f.forecast.cumulative;
  const last = c.netWorth[c.netWorth.length - 1];
  if (last === undefined) return [];
  const end = lastDayOf(last);
  const reached = f.goals.filter((g) => !g.isAchieved && g.estimatedDate !== null && g.estimatedDate > f.today && g.estimatedDate <= end);
  const out: MilestoneView[] = reached.map((g) => ({ goal_id: g.goal.id, label: g.goal.label, date: formatDay(g.estimatedDate!), series: g.goal.series }));
  const debtFree = f.forecast.metrics.debtFreeDate;
  if (debtFree !== null && !reached.some((g) => isDebtFreeGoal(g.goal))) out.push({ goal_id: '', label: 'Debt free', date: formatDay(debtFree), series: 'debt' });
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

export function forecastView(input: ViewInput, options: ForecastOptions = {}) {
  const resolution = options.resolution ?? FORECAST_DEFAULTS.resolution;
  const maxPoints = options.maxPoints ?? FORECAST_DEFAULTS.maxPoints;
  const f = runForecast(input, options.years);
  const years = f.years;
  const line = linePoints(f);
  const c = f.forecast.cumulative;
  // A month's bucket j sits at line index j + 1, or j when today is a month's last day and now replaces it.
  const shift = line.length - c.netWorth.length;
  const indexes =
    resolution === 'monthly'
      ? line.map((_, i) => i)
      : [0, ...Array.from({ length: years }, (_, y) => Math.min((y + 1) * 12 - 1, c.netWorth.length - 1) + shift).filter((i) => i > 0)];
  const truncated = indexes.length > maxPoints;
  const points = indexes.slice(0, maxPoints).map((i) => ({
    date: formatDay(line[i]!.date),
    net_worth: money(line[i]!.netWorth),
    cash: money(line[i]!.cash),
    investments: money(line[i]!.investments),
    property_equity: money(line[i]!.equity),
    debt: money(line[i]!.debt),
  }));
  const m = f.forecast.metrics;
  const now = nowState(f);
  const mortgageFree = mortgageFreeDate(f.plan, f.today);
  return {
    as_of: input.asOf,
    years,
    resolution,
    /** The line starts at now, today (R20). */
    starts: input.asOf,
    now: { date: input.asOf, net_worth: money(now.netWorth) },
    points,
    truncated,
    milestones: milestones(f),
    goals: f.goals.map((g) => goalView(g, f.today)),
    metrics: {
      final_net_worth: money(m.finalNetWorth),
      final_cash: money(m.finalCash),
      final_investments: money(m.finalInvestment),
      // The engine's metric counts only older-format properties; the points' equity series is every property and other asset.
      final_property_equity: money(lastAmount(c.equity)),
      final_debt: money(m.finalDebt),
      peak_net_worth: money(m.peakNetWorth),
      peak_net_worth_date: m.peakNetWorthDate === null ? null : formatDay(core.addDays(m.peakNetWorthDate, -1)),
      lowest_cash: money(m.lowestCash),
      lowest_cash_date: m.lowestCashDate === null ? null : formatDay(core.addDays(m.lowestCashDate, -1)),
      debt_free_date: m.debtFreeDate === null ? null : formatDay(m.debtFreeDate),
      mortgage_free_date: mortgageFree === null ? null : formatDay(mortgageFree),
      monthly_savings: money(m.monthlySavings),
      savings_rate_percent: round1(core.toDouble(m.savingsRate)),
      total_income: money(m.totalIncome),
      total_expenses: money(m.totalExpenses),
      total_debt_payments: money(m.totalDebtPayments),
      total_investment_contributions: money(m.totalInvestmentContributions),
      average_passive_income_monthly: money(m.averagePassiveIncome),
    },
    definitions: definitionsFor('start_of_month', 'month', 'now', 'debt_free', 'mortgage_free', 'goal_progress', 'goals_horizon', 'projection'),
  };
}

export type ForecastResult = ReturnType<typeof forecastView>;
